import React from 'react';
import { Table, Card, Image } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RmaProduct } from 'hooks/refunds/useRmaDetails';
import { currencyFormat } from 'helpers/utils';

interface RmaProductsTableProps {
  products: RmaProduct[];
  className?: string;
}

const RmaProductsTable: React.FC<RmaProductsTableProps> = ({ products = [], className = '' }) => {
  const { t } = useTranslation();
  
  // Early return if no products
  if (!products.length) {
    return (
      <Card className={`shadow-sm rounded-3 border-light ${className}`}>
        <Card.Body className="p-3">
          <p className="text-center mb-0 text-body-tertiary">{t('rmaDetails.messages.noProductsFound')}</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className={`shadow-sm rounded-3 border-light ${className}`}>
      <div className="table-responsive">
        <Table className="mb-0 table-borderless table-hover">
          <thead className="bg-light">
            <tr className="border-bottom">
              <th className="ps-4" scope="col" style={{ width: '70px', fontSize: '0.8rem' }}></th>
              <th className="ps-2" scope="col" style={{ minWidth: '250px', fontSize: '0.8rem' }}>{t('rmaDetails.fields.product')}</th>
              <th className="ps-2 text-center" scope="col" style={{ width: '80px', fontSize: '0.8rem' }}>{t('sku')}</th>
              <th className="text-end" scope="col" style={{ width: '100px', fontSize: '0.8rem' }}>{t('price')}</th>
              <th className="text-center" scope="col" style={{ width: '80px', fontSize: '0.8rem' }}>{t('quantity')}</th>
              <th className="text-end pe-4" scope="col" style={{ width: '120px', fontSize: '0.8rem' }}>{t('orderSummary.total')}</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product, index) => {
              // Calculate total for this product
              const total = product.product_price * product.qty;

              return (
                <tr key={`${product.product_id}-${index}`} className="border-bottom">
                  {/* Product image with improved styling and error handling */}
                  <td className="ps-4 py-3">
                    <Link to={`/products/${product.product_id}`}>
                      <div className="rounded-2 border-0 d-flex align-items-center justify-content-center overflow-hidden shadow-sm" 
                           style={{ width: 50, height: 50, flexShrink: 0 }}>
                        {product.product_image ? (
                          <img 
                            src={product.product_image} 
                            alt={`Product image for ${product.product_name}`}
                            style={{ 
                              maxWidth: '100%', 
                              maxHeight: '100%', 
                              objectFit: 'contain',
                              padding: '2px'
                            }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              // Add a text fallback when image fails to load
                              const parent = target.parentElement;
                              if (parent) {
                                parent.innerHTML = `
                                  <div class="d-flex align-items-center justify-content-center text-center" style="width: 100%; height: 100%; padding: 4px; font-size: 0.65rem; font-weight: 600;">
                                    ${product.product_name.substring(0, 20)}${product.product_name.length > 20 ? '...' : ''}
                                  </div>
                                `;
                              }
                            }}
                          />
                        ) : (
                          <div className="d-flex align-items-center justify-content-center text-center" 
                               style={{ width: '100%', height: '100%', padding: '4px', fontSize: '0.65rem', fontWeight: 600 }}>
                            {product.product_name.substring(0, 20)}{product.product_name.length > 20 ? '...' : ''}
                          </div>
                        )}
                      </div>
                    </Link>
                  </td>
                  
                  {/* Product name and attributes */}
                  <td className="ps-2 py-3">
                    <h6 className="mb-1 fw-semibold">
                      <Link 
                        to={`/products/${product.product_id}`} 
                        className="text-primary-emphasis hover-text-decoration-underline"
                      >
                        {product.product_name}
                      </Link>
                    </h6>
                    
                    {/* Display attributes if any */}
                    {product.attributes && product.attributes.length > 0 && (
                      <div className="mb-2">
                        {product.attributes.map((attr, attrIndex) => (
                          <span key={`attr-${attrIndex}`} className="d-inline-block me-3 mb-1">
                            <span className="text-body-tertiary">{attr.code}: </span>
                            <span className="fw-medium text-body">{attr.value}</span>
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {/* Display reason */}
                    {product.reason && (
                      <div className="fs-9">
                        <span className="text-body-secondary">{t('rmaDetails.fields.reason')}: </span>
                        <span className="text-body">{product.reason}</span>
                      </div>
                    )}
                  </td>
                  
                  {/* Product SKU */}
                  <td className="ps-2 text-center align-middle">
                    <span className="text-body fs-9">{product.product_sku}</span>
                  </td>
                  
                  {/* Unit price */}
                  <td className="text-end align-middle">
                    <span className="fw-semibold fs-9">
                      {currencyFormat(product.product_price, { minimumFractionDigits: 2 })}
                    </span>
                  </td>
                  
                  {/* Quantity */}
                  <td className="text-center align-middle">
                    <span className="fw-semibold fs-9">{product.qty}</span>
                  </td>
                  
                  {/* Total price */}
                  <td className="text-end pe-4 align-middle">
                    <span className="fw-bold fs-9">
                      {currencyFormat(total, { minimumFractionDigits: 2 })}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </Card>
  );
};

export default RmaProductsTable;
